import jwt_decode, { JwtPayload } from 'jwt-decode';
import axios from 'axios';
import { store } from '../redux/store';
import { updateHasSharedIdentity } from '../redux/slices/App.slice';

const HOST_API = process.env.REACT_APP_HOST_API;

/**
 * Helper class for authentication against an EBS service. Allows the storage of a token to be accessed across componenents.
 * This is not meant to be a source of truth. Use only for presentational purposes.
 */
export default class Authentication {
	private static state = {
		token: null,
		helixToken: null,
		clientId: null,
		user_id: '',
		username: '',
		display_name: '',
		isMod: false,
		role: '',
		avatar: '',
	};

	// Received from onAuthorized
	private static tokens = {
		token: '',
		helixToken: '',
		clientId: '',
		channelId: '',
		userId: '',
	};

	static isLoggedIn() {
		return !!Authentication.state.user_id;
	}

	// This does guarantee the user is a moderator- this is fairly simple to bypass - so pass the JWT and verify
	// server-side that this is true. This, however, allows you to render client-side UI for users without holding on a backend to verify the JWT.
	// Additionally, this will only show if the user shared their ID, otherwise it will return false.
	static isModerator() {
		return Authentication.state.isMod;
	}

	static isBroadcaster() {
		return Authentication.state.role === 'broadcaster';
	}

	static getUserId() {
		return Authentication.state.user_id;
	}

	static getOpaqueId() {
		return Authentication.tokens.userId;
	}

	static async getUsername() {
		if (!Authentication.tokens.helixToken) {
			return null;
		}

		const headers: any = {
			'client-Id': Authentication.tokens.clientId,
			Authorization: `Extension ${Authentication.tokens.helixToken}`,
		};

		const userRes = await axios(`https://api.twitch.tv/helix/users?id=${Authentication.state.user_id}`, {
			headers,
		});
		Authentication.state.username = userRes.data?.data?.[0]?.login;
		Authentication.state.display_name = userRes.data.data?.[0]?.display_name;
		Authentication.state.avatar = userRes.data.data?.[0]?.profile_image_url;
		return Authentication.state.username;
	}

	// Set the token in the Authentication componenent state this is naive, and will work with whatever token is returned. under no circumstances should you use this logic to trust private data- you should always verify the token on the backend before displaying that data.
	static setToken(tokens: { token: string; clientId: string; helixToken: string; channelId: string; userId }) {
		let isMod = false;
		let role = '';
		let user_id = '';

		try {
			Authentication.tokens = {
				...tokens,
			};
			const decoded: any = jwt_decode<JwtPayload>(tokens.token);

			if (decoded.role === 'broadcaster' || decoded.role === 'moderator') {
				isMod = true;
			}

			user_id = decoded.user_id;
			role = decoded.role;
		} catch (e) {}

		Authentication.state = {
			...Authentication.state,
			isMod,
			user_id,
			role,
		};

		Authentication.getUsername();

		store.dispatch(updateHasSharedIdentity(Authentication.isLoggedIn()));
	}

	// checks to ensure there is a valid token in the state
	static isAuthenticated() {
		if (Authentication.tokens.token && Authentication.tokens.userId) {
			return true;
		} else {
			return false;
		}
	}

	/**
	 * Makes a call against a given endpoint using a specific method.
	 *
	 * Returns a Promise with the Request() object per fetch documentation.
	 *
	 */
	static makeCall(url, method: 'GET' | 'POST' | 'DELETE' | 'PUT' = 'GET'): Promise<any> {
		return new Promise((resolve, reject) => {
			if (Authentication.isAuthenticated()) {
				const headers: any = {
					Authorization: `Bearer ${Authentication.tokens.token}`,
					lsorigin: 'twitch',
				};

				axios(`${HOST_API}/${url}`, {
					method,
					headers,
				})
					.then((response) => resolve(response))
					.catch((e) => reject(e));
			} else {
				reject('Unauthorized');
			}
		});
	}

	/**
	 * Checks if command can be called before bits are processed
	 *
	 * Returns a Promise with the Request() object per fetch documentation.
	 *
	 */
	static checkCommandCall({ user_id, extensions_id, command_id, command_name, amount, free_ticket_id = null }): Promise<any> {
		return new Promise((resolve, reject) => {
			if (Authentication.isAuthenticated() && Authentication.isLoggedIn()) {
				const headers: any = {
					Authorization: `Bearer ${Authentication.tokens.token}`,
					lsorigin: 'twitch',
				};

				axios
					.post(
						`${HOST_API}/api/extension/check-command`,
						{
							origin: 'twitch',
							user_id,
							extensions_id,
							site_user_id: Authentication.state.user_id,
							command_id,
							command_name,
							amount_type: 'bits',
							amount,
							free_ticket_id,
							is_test: this.isBroadcaster(),
						},
						{ headers },
					)
					.then((response) => resolve(response))
					.catch((e) => reject(e));
			} else {
				reject('Unauthorized');
			}
		});
	}

	/**
	 * Makes a call to a specified command
	 *
	 * Returns a Promise with the Request() object per fetch documentation.
	 *
	 */
	static commandCall({
		user_id,
		extensions_id,
		command_name,
		command_id,
		command_cooldown_on,
		command_cooldown_type,
		command_cooldown_message,
		command_cooldown_duration,
		bits,
		prompt,
		is_hudfx,
		hudfx_id,
		free_ticket_id = null,
		transactionId,
		transactionReceipt,
	}: {
		user_id: number | null;
		extensions_id: number | null;
		command_name?: string;
		command_id?: string;
		command_cooldown_on?: boolean;
		command_cooldown_type?: string;
		command_cooldown_message?: string;
		command_cooldown_duration?: number;
		bits?: number;
		prompt?: boolean;
		is_hudfx?: boolean;
		hudfx_id?: string;
		free_ticket_id: number | null;
		transactionId?: string;
		transactionReceipt?: string;
	}): Promise<any> {
		return new Promise((resolve, reject) => {
			if (Authentication.isAuthenticated() && Authentication.isLoggedIn()) {
				const headers: any = {
					Authorization: `Bearer ${Authentication.tokens.token}`,
					lsorigin: 'twitch',
				};

				axios
					.post(
						`${HOST_API}/api/extension/command`,
						{
							origin: 'twitch',
							user_id,
							extensions_id,
							is_broadcaster: this.isBroadcaster(),
							broadcaster_id: Authentication.tokens.channelId,
							site_user_id: Authentication.state.user_id,
							site_avatar: Authentication.state.avatar,
							site_opaque_id: Authentication.tokens.userId,
							site_username: Authentication.state.username,
							site_display_name: Authentication.state.display_name,
							command_id,
							command_name,
							command_cooldown_on,
							command_cooldown_type,
							command_cooldown_message,
							command_cooldown_duration,
							amount_type: 'bits',
							amount: bits,
							bits,
							prompt,
							is_hudfx,
							hudfx_id,
							free_ticket_id,
							is_test: this.isBroadcaster(),
							transactionId,
							transactionReceipt,
						},
						{ headers },
					)
					.then((response) => resolve(response))
					.catch((e) => reject(e));
			} else {
				reject('Unauthorized');
			}
		});
	}
}
